import {
  InteractionDto,
  PendingItemDto,
} from '@act/shared/data-transfer-objects';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';
import { selectCallsByGuideAndTime } from '../../slices/callsSlice';
import {
  selectInteractionsLoadingState,
  selectPriorityContactLoadingState,
  selectPriorityContactsByGuide,
} from '../../slices/docketSlice';
import { selectMessagesByGuideAndTime } from '../../slices/messagesSlice';
import { selectNotesByGuideAndTime } from '../../slices/notesSlice';
import { selectVisitsByGuideAndTime } from '../../slices/visitsSlice';
import { useAppSelector } from '../../store-hooks';
import {
  selectLoadingPendingItemsForGuide,
  selectPendingItemsForGuide,
} from './pending-item-selectors';
import { ActivitySelectorParams, GuideSelectorParams } from '../../types/selector-types';

export const selectGuideActivity: (
  state,
  paramObj: ActivitySelectorParams
) => InteractionDto[] = createSelector(
  [
    selectVisitsByGuideAndTime,
    selectCallsByGuideAndTime,
    selectNotesByGuideAndTime,
    selectMessagesByGuideAndTime,
  ],
  (visits, calls, notes, messages) => {
    const interactions: InteractionDto[] = [
      ...visits,
      ...calls,
      ...notes,
      ...messages,
    ];
    interactions.sort((a, b) => b.timestamp - a.timestamp);
    return interactions;
  }
);

interface DocketPendingSelectorParams {
  guideId: string;
  startEpochTime: number;
  endEpochTime: number;
  includeNull: boolean;
  [key: string]: unknown;
}

const selectPendingItemsForGuideInTimeRange: (
  state,
  paramObj: DocketPendingSelectorParams
) => PendingItemDto[] = createSelector(
  [
    selectPendingItemsForGuide,
    (_, paramObj: DocketPendingSelectorParams) => paramObj.startEpochTime,
    (_, paramObj: DocketPendingSelectorParams) => paramObj.endEpochTime,
    (_, paramObj: DocketPendingSelectorParams) => paramObj.includeNull,
  ],
  (pendingItems, startEpochTime, endEpochTime, includeNull) => {
    return pendingItems.filter(
      p =>
        (includeNull && !p.timestamp) ||
        (p.timestamp >= startEpochTime && p.timestamp <= endEpochTime)
    );
  }
);

export const selectLatePendingItemsForGuide: (
  state,
  paramObj: GuideSelectorParams
) => PendingItemDto[] = createSelector(
  selectPendingItemsForGuide,
  pendingItems => {
    const startOfDay = DateTime.now().startOf('day').toMillis();
    return pendingItems.filter(p => p.timestamp && p.timestamp < startOfDay);
  }
);

export const useDocketSelectors = {
  guideActivity: (params: ActivitySelectorParams) =>
    useAppSelector(state => selectGuideActivity(state, params)),
  pendingItemsForGuideInTimeRange: (params: DocketPendingSelectorParams) =>
    useAppSelector(state =>
      selectPendingItemsForGuideInTimeRange(state, params)
    ),
  pendingItemsLoading: () => useAppSelector(selectLoadingPendingItemsForGuide),
  latePendingItemsForGuide: (guideId: string) =>
    useAppSelector(state => selectLatePendingItemsForGuide(state, { guideId })),
  interactionsLoadingState: () =>
    useAppSelector(selectInteractionsLoadingState),
  priorityContactsLoadingState: () =>
    useAppSelector(selectPriorityContactLoadingState),
  selectPriorityContactsByGuide: (guideId: string) =>
    useAppSelector(state => selectPriorityContactsByGuide(state, { guideId })),
};
